import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import * as service from '../../services/bauordnungService';
import type { SchreibKontext } from '../../services/bauordnungService';
import { queueJournalMirror } from '../../services/serviceJournal';
import type { Report } from '../../types';
import { pruefeSchreiben } from '../bestandteile/bestandteileDialogLogic';
import type { SchreibSperre } from '../bestandteile/abschlussSchreibweg';
import {
    MENGE_JE_DOKUMENT, entscheideNachRiegel, istPaketZiel, planeBauordnungBuchung, ruecknahmeMeldung,
} from './bauordnungBooking';
import { DOKUMENTE, type Dokumentart } from './kapitel';
import type { Bauordnung } from './types';

/**
 * Die Leistungsbuchung der Baustellenordnung bzw. des HSE-Plans — im MODUL,
 * nicht im Reiter (Plan Paketposition B5).
 *
 * Bis hierhin lag sie im Reiter „Ausgabe & Freigabe". Seit die Buchung direkt
 * nach der Freigabe angeboten wird (Abschlussdialog des Moduls) und weiter im
 * Reiter steht, gehoert sie dem Container: EIN Lauf, EINE Meldung, und ein
 * Reiterwechsel waehrend der Buchung verliert weder Sperre noch Ergebnis
 * (dieselbe Ueberlegung wie bei `useFreigabeLauf`).
 *
 * Reihenfolge (Spezifikation J): zuerst der Riegel in der Datenbank
 * (`markiereGebucht`, Bedingung IM UPDATE), bei `false` abbrechen, erst dann
 * die Gutschrift ins Projekt — mit der Zweitschrift ins Buchungsjournal
 * (`queueJournalMirror`, Quelle 'bauordnung', Muster B).
 *
 * ═══ AN DAS PROJEKT GEBUNDEN, MIT KOMPENSATION (Codex R2b/R2c) ═════════════
 * Das Projekt des Vorgangs ist das der Baustellenordnung (`o.project_id`).
 * Vor dem Riegel und vor dem Schreiben prueft `pruefeSchreiben` (D, eine
 * Regel fuer Hub und Module): eingehaengt, der aktuelle Stand gehoert zu
 * diesem Projekt, der zu schreibende auch. Gelesen und geschrieben wird ueber
 * `holeBericht`/`schreibeFuer` aus `useBestandteilAbschluss` — dieselbe Ref,
 * ueber die der Abschlussschritt gleichzeitig ein Paket bucht.
 *
 * Kann die Gutschrift NACH dem Riegel nicht geschrieben werden (Modul
 * verlassen, Projekt gewechselt, Position fehlt), wird der eigene Vermerk
 * bedingt zurueckgenommen (`nimmGebuchtZurueck`, nur mit GENAU unserem
 * Stempel) — sonst galte das Dokument als gebucht, ein neuer Versuch liefe in
 * den Riegel, und die Leistung waere verloren. Scheitert auch das: laute
 * Meldung und `bug_reports`.
 *
 * Refs und Einhaenge-Merker in `useLayoutEffect` (synchron im Commit, vor
 * jeder Netzantwort) — nicht in `useEffect`.
 */

type Schreiben = <T>(vorgang: () => Promise<T>) => Promise<T | null>;

export type BuchungsTon = 'ok' | 'teils' | 'fehler';

/**
 * Was ein Buchungsversuch ergab — der gefuehrte Dialog braucht das Ergebnis,
 * um bei einer ausgebliebenen Buchung den Abschlussdialog zu oeffnen statt
 * die Position still ungebucht zu lassen (Opus-Review 02.11.2026):
 *  - `gebucht`: Riegel und Gutschrift geschrieben;
 *  - `bereits`: der Riegel griff — war schon gebucht (oder nicht mehr freigegeben);
 *  - `nicht`: Paketposition, Sperre, Dienstfehler oder zurueckgenommener Vermerk — die Meldung sagt es;
 *  - `uebersprungen`: gar nicht versucht (laeuft schon, Nur-Ansicht, kein Leistungsstand).
 */
export type BuchungsErgebnis = 'gebucht' | 'bereits' | 'nicht' | 'uebersprungen';

export interface BauordnungBuchungAnsicht {
    /** Darf hier gebucht werden? Ohne `report`/`onUpdate` oder in der Nur-Ansicht: nein. */
    darfBuchen: boolean;
    /** Welches Dokument gerade gebucht wird — sperrt die Knoepfe. */
    laeuft: Dokumentart | null;
    meldung: { ton: BuchungsTon; text: string } | null;
    buche: (o: Bauordnung, art: Dokumentart, serviceId: string) => Promise<BuchungsErgebnis>;
}

/** Meldet eine gescheiterte Ruecknahme in `bug_reports` (Hintergrundkanal, wirft nie). */
const meldeRuecknahmeFehler = async (o: Bauordnung, art: Dokumentart, grund: unknown): Promise<void> => {
    if (typeof window === 'undefined') return;
    try {
        const { meldeImHintergrund } = await import('../../services/bugReportService');
        await meldeImHintergrund({
            message: `[Bauordnung] Buchungsvermerk ohne Gutschrift, Rücknahme gescheitert: ${art}`,
            stack: JSON.stringify({ projectId: o.project_id, ordnungId: o.id, dokument: art, grund: String((grund as Error)?.message ?? grund) }),
            source: 'window',
        });
    } catch {
        // Die Meldung ist eine Zugabe — sie darf nie werfen.
    }
};

export const useBauordnungBuchung = (a: {
    /** Gibt es einen Leistungsstand und einen Schreibweg (`report` UND `onUpdate`)? */
    kannSchreiben: boolean;
    /** Der juengste Stand des Moduls (`useBestandteilAbschluss.holeBericht`). */
    holeBericht: () => Report | null;
    /** Schreibt fuer ein Projekt — oder verwirft mit Grund (`useBestandteilAbschluss.schreibeFuer`). */
    schreibeFuer: (projektId: string, r: Report) => SchreibSperre | null;
    schreiben: Schreiben;
    ctx: SchreibKontext;
}): BauordnungBuchungAnsicht => {
    const { kannSchreiben, holeBericht, schreibeFuer, schreiben, ctx } = a;
    const aktuell = useRef(a);
    useLayoutEffect(() => {
        aktuell.current = { kannSchreiben, holeBericht, schreibeFuer, schreiben, ctx };
    }, [kannSchreiben, holeBericht, schreibeFuer, schreiben, ctx]);

    const eingehaengt = useRef(true);
    useLayoutEffect(() => {
        eingehaengt.current = true;
        return () => { eingehaengt.current = false; };
    }, []);

    const [laeuft, setLaeuft] = useState<Dokumentart | null>(null);
    const [meldung, setMeldung] = useState<{ ton: BuchungsTon; text: string } | null>(null);
    // Sofort sichtbar fuer einen Doppelklick — der Zustand kaeme erst mit dem naechsten Render.
    const laeuftRef = useRef(false);

    const darfBuchen = kannSchreiben && !ctx.nurAnsicht;

    const buche = useCallback(async (o: Bauordnung, art: Dokumentart, serviceId: string): Promise<BuchungsErgebnis> => {
        const start = aktuell.current;
        if (laeuftRef.current || !start.kannSchreiben || start.ctx.nurAnsicht || !start.holeBericht()?.metadata?.globalData) return 'uebersprungen';
        laeuftRef.current = true;
        const zeige = (m: { ton: BuchungsTon; text: string } | null) => { if (eingehaengt.current) setMeldung(m); };
        if (eingehaengt.current) setLaeuft(art);
        zeige(null);
        const titel = DOKUMENTE[art].titel;
        /** Das Projekt des Vorgangs — das der Baustellenordnung, beim Start festgehalten. */
        const projektId = String(o.project_id ?? '').trim();
        /** Die Wache vor dem Riegel und vor dem Schreiben (D's Regel). */
        const sperreJetzt = (): SchreibSperre | null => {
            const stand = aktuell.current.holeBericht();
            return pruefeSchreiben({ eingehaengt: eingehaengt.current, projektId, neu: stand, aktuell: stand });
        };
        try {
            // 0) Vor dem Riegel: kein Paket (Kernregel 7) und der Stand gehoert
            //    zu diesem Projekt — sonst bliebe ein Dokument als gebucht
            //    vermerkt, dessen Gutschrift danach verweigert wuerde.
            if (istPaketZiel(start.holeBericht()?.metadata?.globalData?.services, serviceId)) {
                zeige({
                    ton: 'fehler',
                    text: `${titel}: die gewählte Position ist eine Paketposition mit Bestandteilen. Sie wird nicht von hier gebucht, `
                        + 'sondern sobald alle Bestandteile erledigt sind.',
                });
                return 'nicht';
            }
            if (sperreJetzt()) {
                zeige({ ton: 'fehler', text: `${titel}: der Projektstand gehört nicht zu dieser Baustellenordnung. Es wurde nichts gebucht.` });
                return 'nicht';
            }

            // 1) DER RIEGEL ZUERST — mit unserem Stempel, damit eine Kompensation
            //    genau DIESEN Vermerk zuruecknehmen kann. `false`: war schon
            //    gebucht oder die Fassung ist nicht mehr freigegeben — es wurde
            //    NICHTS geschrieben. `null`: der Dienst hat geworfen, `schreiben`
            //    zeigt den Fehler schon.
            const stempel = new Date().toISOString();
            const hatGebucht = await start.schreiben(() => service.markiereGebucht(o.id, art, serviceId, MENGE_JE_DOKUMENT, start.ctx, stempel));
            if (hatGebucht === null) return 'nicht';
            if (!hatGebucht) {
                zeige({
                    ton: 'teils',
                    text: `${titel}: war bereits gebucht oder die Fassung ist nicht mehr freigegeben. Es wurde nichts doppelt gebucht.`,
                });
                return 'bereits';
            }

            // 2) Die Gutschrift — auf dem Stand, der JETZT gilt, nur fuer DIESES
            //    Projekt (schreibeFuer prueft beim Schreiben noch einmal).
            const sperre = sperreJetzt();
            const bericht = aktuell.current.holeBericht();
            const plan = !sperre && bericht ? planeBauordnungBuchung(bericht, serviceId, {
                dokument: art,
                versionLabel: o.version_label,
                freigegebenAm: o.freigegeben_am,
                performerName: start.ctx.handelnder,
            }) : null;
            const verworfen = plan ? aktuell.current.schreibeFuer(projektId, plan.report) : null;

            if (entscheideNachRiegel({ sperre, planGefunden: !!plan, verworfen }) === 'gutschreiben' && plan) {
                // Muster B: vormerken — angehaengt wird nach dem bestaetigten Flush.
                if (plan.vormerkung) queueJournalMirror(plan.vormerkung);
                else console.warn('[Bauordnung] Buchung ohne belastbare Projekt-id, nicht im Journal vorgemerkt.');
                zeige({ ton: 'ok', text: `${titel}: ${MENGE_JE_DOKUMENT} Pauschal gebucht.` });
                return 'gebucht';
            }

            // 3) KOMPENSATION: die Gutschrift kam nicht ins Projekt — den eigenen
            //    Vermerk bedingt zuruecknehmen. Direkt ueber den Dienst (das
            //    Modul kann schon weg sein); steht es noch, laedt `schreiben`
            //    das Buendel danach neu.
            const grund = verworfen ?? sperre ?? 'position_fehlt';
            console.warn(`[Bauordnung] ${titel}: Gutschrift nicht geschrieben (${grund}), Buchungsvermerk wird zurückgenommen.`);
            let zurueck = false;
            let fehler: unknown = null;
            try {
                zurueck = await service.nimmGebuchtZurueck(o.id, art, stempel, start.ctx);
            } catch (e) {
                fehler = e;
            }
            if (zurueck) {
                if (eingehaengt.current) await aktuell.current.schreiben(async () => true);
                zeige({ ton: 'teils', text: ruecknahmeMeldung(titel, 'zurueckgenommen') });
                return 'nicht';
            }
            console.error(`[Bauordnung] ${titel}: Buchungsvermerk ohne Gutschrift, Rücknahme gescheitert.`, fehler);
            void meldeRuecknahmeFehler(o, art, fehler ?? 'Vermerk trug einen anderen Stempel oder war schon weg');
            zeige({ ton: 'fehler', text: ruecknahmeMeldung(titel, 'fehlgeschlagen') });
            return 'nicht';
        } finally {
            laeuftRef.current = false;
            if (eingehaengt.current) setLaeuft(null);
        }
    }, []);

    return { darfBuchen, laeuft, meldung, buche };
};
